import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api/prospects';
import { isStandalonePublicRoute } from '../access/publicRoutes';
import {
  clearStoredActiveApp,
  computeAccessProfile,
  getAvailableApps,
  hasMultipleApps,
  readStoredActiveApp,
  writeStoredActiveApp,
} from '../access/userAccess';

const UserAccessContext = createContext(null);

export function UserAccessProvider({ children }) {
  const location = useLocation();
  const isPublicStandalone = isStandalonePublicRoute(location.pathname);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeApp, setActiveAppState] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch('/me');
      setUser(res?.data?.user || null);
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPublicStandalone) {
      setLoading(false);
      setChooserOpen(false);
      return;
    }
    refreshUser();
  }, [refreshUser, isPublicStandalone]);

  useEffect(() => {
    if (isPublicStandalone || !user || loading) return;
    const profile = computeAccessProfile(user);
    const apps = getAvailableApps(user);
    const stored = readStoredActiveApp();

    if (stored && apps.includes(stored)) {
      setActiveAppState(stored);
      setChooserOpen(false);
      return;
    }

    if (profile.kind === 'hubup_only') {
      writeStoredActiveApp('hubup');
      setActiveAppState('hubup');
      setChooserOpen(false);
      return;
    }
    if (profile.kind === 'crm_only') {
      writeStoredActiveApp('crm');
      setActiveAppState('crm');
      setChooserOpen(false);
      return;
    }
    if (profile.kind === 'dual') {
      setActiveAppState(null);
      setChooserOpen(true);
    }
  }, [user, loading, isPublicStandalone]);

  const chooseAppMode = useCallback((mode) => {
    writeStoredActiveApp(mode);
    setActiveAppState(mode);
    setChooserOpen(false);
  }, []);

  const openAppChooser = useCallback(() => {
    if (!user) return;
    if (!hasMultipleApps(user)) return;
    clearStoredActiveApp();
    setActiveAppState(null);
    setChooserOpen(true);
  }, [user]);

  const profile = useMemo(() => computeAccessProfile(user), [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      profile,
      activeApp,
      chooserOpen,
      setChooserOpen,
      chooseAppMode,
      openAppChooser,
      refreshUser,
    }),
    [
      user,
      loading,
      profile,
      activeApp,
      chooserOpen,
      chooseAppMode,
      openAppChooser,
      refreshUser,
    ],
  );

  return (
    <UserAccessContext.Provider value={value}>
      {children}
    </UserAccessContext.Provider>
  );
}

export function useUserAccess() {
  const ctx = useContext(UserAccessContext);
  if (!ctx) {
    throw new Error('useUserAccess must be used within UserAccessProvider');
  }
  return ctx;
}
